import axios from "axios";
import moment from "moment";
import { Logs } from "lucide-react";
import { useAuth } from "context/useAuth";
import { useEffect, useState } from "react";
import ViewMgtDevicePanel from "../ViewMgtDevicePanel";
import { useAppSettings } from "context/appsettingsContext";
import * as deviceStatusUtils from "../../../../../functions/deviceStatusUtils";

function hasValue(value: any) {
  return value !== null && value !== undefined && value !== "";
}

function transformEvent(event: any) {
  const e = { ...event };

  if (hasValue(e.requestLock)) {
    e.requestLock = e.requestLock ? "LOCK" : "UNLOCK";
  }

  if (hasValue(e.requestLockType)) {
    e.requestLockType = deviceStatusUtils.LockType[e.requestLockType] ?? e.requestLockType;
  }

  if (hasValue(e.source)) {
    e.source = deviceStatusUtils.LockCommandSource[e.source] ?? e.source;
  }

  if (hasValue(e.tamperType)) {
    e.tamperType = deviceStatusUtils.TamperType[e.tamperType] ?? e.tamperType;
  }

  if (hasValue(e.currentState)) {
    e.currentState = deviceStatusUtils.SystemState[e.currentState] ?? e.currentState;
  }

  return e;
}

export default function ViewMgtDeviceStatusEvents({ deviceId, device }: { deviceId?: string | null | undefined; device?: any | undefined | null; }) {
  
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deviceEvents, setDeviceEvents] = useState<any[]>([]);
  const [newEventTimestamps, setNewEventTimestamps] = useState<Set<any>>(new Set());
  const lastMessageTime = device?.lastMessageTimestamp?.toMillis ? device.lastMessageTimestamp.toMillis() : null;

  useEffect(() => {

    if (!deviceId || !authorisedUser?.accessToken || !settings?.deviceEventsUrl) {
      return;
    }

    const controller = new AbortController();

    (async function() {
        try {
            setLoading(true);

            const newestTimestamp = deviceEvents.length > 0 ? deviceEvents[0]?.logTimestamp : null;

            const response = await axios.get(settings.deviceEventsUrl, {
                signal: controller.signal,
                headers: {
                    Authorization: `Bearer ${authorisedUser.accessToken}`,
                    "Content-Type": "application/json",
                },
                params: {
                    "serial-number": deviceId,
                    "order": "desc",
                    "page-size": 500,
                    "page-index": 0,
                },
            });

            const incoming = response.data.logs || [];

            if (!newestTimestamp) {
              setDeviceEvents(incoming);
              return;
            }

            const newEntries: any[] = [];

            for (const e of incoming) {
              if (!e) {
                continue;
              }

              if (e.logTimestamp === newestTimestamp) {
                break;
              }

              newEntries.push(e);
            }

            if (newEntries.length === 0) {
              return;
            }

            setDeviceEvents((current) => {

              const seen = new Set(current.map((x) => x?.logTimestamp).filter((x) => x !== null && x !== undefined));

              const mergedNewEntries: any[] = [];

              for (const e of newEntries) {
                if (!seen.has(e.logTimestamp)) {
                  mergedNewEntries.push(e);
                }
              }

              if (mergedNewEntries.length === 0) {
                return current;
              }

              setNewEventTimestamps((prev) => {
                const next = new Set(prev);
                for (const e of mergedNewEntries) {
                  next.add(e.logTimestamp);
                }
                return next;
              });

              setTimeout(() => {
                setNewEventTimestamps((prev) => {
                  const next = new Set(prev);
                  for (const e of mergedNewEntries) {
                    next.delete(e.logTimestamp);
                  }
                  return next;
                });
              }, 900);

              return [...mergedNewEntries, ...current];
            });

        } catch (error: any) {
            if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
                return;
            }
            console.error("Error fetching device events:", error);
        } finally {
            setLoading(false);
        }
    })();

    return () => controller.abort();

  }, [deviceId, lastMessageTime, authorisedUser?.accessToken, settings?.deviceEventsUrl]);

  return (
    <ViewMgtDevicePanel title="Logs" icon={<Logs size={16} />} fullWidth>

      <style>
        {`
          @keyframes subtleNewRow {
            0%   { background-color: rgba(59, 130, 246, 0.18); }
            100% { background-color: transparent; }
          }

          .new-row-once {
            animation: subtleNewRow 900ms ease-out 1;
          }
        `}
      </style>

      <div className="p-3 space-y-4">
        <div className="border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 font-small">
              <tr>
                <th>Timestamp</th>
                <th>Type</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
                {deviceEvents.map((event) => (
                    <tr key={event.id} data-event-id={event.id} className={newEventTimestamps.has(event.logTimestamp) ? "new-row-once" : ""}>
                    <td>{moment(event.logTimestamp).local().format('DD MMM YYYY hh:mm:ss A')}</td>
                    <td>{event.logType}</td>
                    <td className="text-xs"><pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(transformEvent(event), null, 2)}</pre></td>
                    </tr>
                ))}
            </tbody>
          </table>

          {loading && (
            <div className="p-3 text-center text-gray-500 text-sm">Loading Events</div>
          )}

          {!loading && deviceEvents.length === 0 && (
            <div className="p-3 text-center text-gray-400 text-sm italic">No Events</div>
          )}

        </div>
      </div>
    </ViewMgtDevicePanel>
  );
}
